import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  VpbBerekening
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {debounceTime, distinctUntilChanged, filter, merge, Observable, shareReplay, switchMap, tap} from "rxjs";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-belasting-bedrag',
  templateUrl: './belasting-bedrag.component.html',
  styleUrls: ['./belasting-bedrag.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class BelastingBedragComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte?.belastingBedrag) {
          this.belastingBedragFg.patchValue(data.aangifte.belastingBedrag, {emitEvent: false});
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly vpbBerekening$: Observable<VpbBerekening> =
    this.dataService.vpbBerekening$.pipe(
      filter((data): data is VpbBerekening => data != null),
      tap(data => {
        this.belastbaarBedragFg.patchValue(data.belastbaarBedrag!, {emitEvent: false});
        this.belastingBerekeningFg.patchValue(data.belasting!, {emitEvent: false});
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public belastingBedragFg = new FormGroup({
    berekendeDeelnemingsverrekening: new FormControl<number | null>(null),
    aftrekEldersBelast: new FormControl<number | null>(null),
    dividendbelasting: new FormControl<number | null>(null),
    kansspelbelasting: new FormControl<number | null>(null),
    voorlopigeAanslag: new FormControl<number | null>(null),
  });

  public belastingBerekeningFg = new FormGroup({
    belastingNaarNormaalTarief: new FormControl<number | null>(null),
    saldoBelastingVoorAftrekEldersBelast: new FormControl<number | null>(null),
    vermeederingBelastingAfkoopPensioen: new FormControl<number | null>(null),
    vermeederingBelastingStamrecht: new FormControl<number | null>(null),
    vermeederingBelastingStatusverliesVrijstelling: new FormControl<number | null>(null),
    aftrekEldersBelast: new FormControl<number | null>(null),
    naarVolgendJaarOverTeBrengenDeelnemingsverrekening: new FormControl<number | null>(null),
    verrekendeDeelnemingsverrekening: new FormControl<number | null>(null),
    naarVolgendJaarOverTeBrengenOndernemingswinst: new FormControl<number | null>(null),
    verrekendeBuitenlandseOndernemingswinst: new FormControl<number | null>(null),
    totaleBelastingverminderingen: new FormControl<number | null>(null),
    totaalVoorheffingen: new FormControl<number | null>(null),
    saldoBelastingDitBoekjaar: new FormControl<number | null>(null),
    alBetaaldeVoorlopigeAanslag: new FormControl<number | null>(null),
    verschuldigdeVennootschapsbelasting: new FormControl<number | null>(null),
  });

  public belastbaarBedragFg = new FormGroup({
    winstVoorSplitsingBelangenwijziging: new FormControl<number | null>(null),
    belastbaarInkomenAbNederlandseVennootschap: new FormControl<number | null>(null),
    belastbareWinstNederlandseOnderneming: new FormControl<number | null>(null),
    belastbareWinst: new FormControl<number | null>(null),
    teVerrekenenGewoneVerliezenDitBoekjaar: new FormControl<number | null>(null),
    teVerrekenenHoudsterverliezenDitBoekjaar: new FormControl<number | null>(null),
    totaalTeVerrekenenVerlies: new FormControl<number | null>(null),
    belastbaarBedrag: new FormControl<number | null>(null),
    omrekenfactorFunctioneleValuta: new FormControl<string | null>(null),
    grondslagFunctioneleValuta: new FormControl<number | null>(null),
    nogTeVerrekenenGewoneVerliezen: new FormControl<number | null>(null),
    nogTeVerrekenenHoudsterVerliezen: new FormControl<number | null>(null),
  });

  ngOnInit(): void {
    merge(
      this.belastingBedragFg.controls.aftrekEldersBelast.valueChanges,
      this.belastingBedragFg.controls.dividendbelasting.valueChanges,
      this.belastingBedragFg.controls.kansspelbelasting.valueChanges,
      this.belastingBedragFg.controls.voorlopigeAanslag.valueChanges
    ).pipe(
      debounceTime(500),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      tap(() => this.saveForm()),
      switchMap(() => this.dataService.calculateLatestCorporateTaxReturn()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.belastingBedragFg.updateValueAndValidity({ emitEvent: false });
    const belastingBedrag = this.belastingBedragFg.getRawValue();

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          belastingBedrag: {
            ...currentEntity.aangifte.belastingBedrag,
            berekendeDeelnemingsverrekening: belastingBedrag.berekendeDeelnemingsverrekening ?? undefined,
            aftrekEldersBelast: belastingBedrag.aftrekEldersBelast ?? undefined,
            dividendbelasting: belastingBedrag.dividendbelasting ?? undefined,
            kansspelbelasting: belastingBedrag.kansspelbelasting ?? undefined,
            voorlopigeAanslag: belastingBedrag.voorlopigeAanslag ?? undefined,
          },
        },
      };
    });
  }
}
